import { MediaListLink } from '@components/FailedMediaList/MediaListLink';
import { formatRelativeTime } from '@lib/utils/relativeTime';
import {
  CheckCircleRounded,
  CloseRounded,
  ErrorRounded,
  NotificationsNoneRounded,
} from '@mui/icons-material';
import { NotifBellEntry, notificationStore } from '@stores/Notifications';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import css from './NotifBell.module.scss';
import { NotifPanelHandlers } from './useNotifActivity';

function VariantIcon({ variant }: { variant: 'success' | 'error' }) {
  return variant === 'success' ? <CheckCircleRounded /> : <ErrorRounded />;
}

const Card = observer(function Card({
  entry,
  onDismiss,
  onOpenDetails,
}: {
  entry: NotifBellEntry;
  onDismiss: () => void;
  /** Called when the user clicks the row's "More info" affordance.
   * Only invoked when `entry.details` is set. */
  onOpenDetails: () => void;
}) {
  return (
    <div className={css.item}>
      <div className={css.icon} data-variant={entry.variant}>
        <VariantIcon variant={entry.variant} />
      </div>
      <div className={css.body}>
        <div className={css.title}>{entry.header}</div>
        {entry.description && <div className={css.desc}>{entry.description}</div>}
        <div className={css.time}>{formatRelativeTime(entry.timestamp)}</div>
        {entry.details && (
          <MediaListLink
            variant={entry.variant}
            action="More info"
            subtext={
              entry.details.kind === 'media-list' && entry.details.filenames.length > 0
                ? `${entry.details.filenames.length} ${
                    entry.details.filenames.length === 1 ? 'item' : 'items'
                  }`
                : undefined
            }
            onClick={onOpenDetails}
          />
        )}
      </div>
      <button className={css.dismiss} onClick={onDismiss} aria-label="Dismiss notification">
        <CloseRounded />
      </button>
    </div>
  );
});

/**
 * The notifications-surface body: "Clear all" head + the card list / empty
 * state. Shell-agnostic — rendered inside the desktop popover AND the mobile
 * drawer's Notifications tab.
 */
export const NotifPanelBody = observer(function NotifPanelBody({
  handlers,
  fullWidth,
}: {
  handlers: NotifPanelHandlers;
  /** Fill the container (mobile drawer) instead of the fixed popover width. */
  fullWidth?: boolean;
}) {
  const { onDismiss, onOpenDetails } = handlers;

  const entries = notificationStore.notifications.filter(
    (n): n is NotifBellEntry => n.type === 'notif-bell-entry',
  );
  // Newest first.
  const sortedEntries = [...entries].sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div className={classNames(css.panel, fullWidth && css.panelDrawer)}>
      <div className={css.panelHead}>
        <span className={css.panelTitle}>Notifications</span>
        <button
          className={css.clearBtn}
          onClick={() => notificationStore.clearNotifBell()}
          disabled={sortedEntries.length === 0}
        >
          Clear all
        </button>
      </div>
      <div className={css.panelList}>
        {sortedEntries.length === 0 ? (
          <div className={css.empty}>
            <NotificationsNoneRounded />
            <div className={css.emptyTitle}>No notifications</div>
            <div className={css.emptySub}>Recent activity will appear here.</div>
          </div>
        ) : (
          sortedEntries.map((entry) => (
            <Card
              key={entry.id}
              entry={entry}
              onDismiss={() => onDismiss(entry)}
              onOpenDetails={() => entry.details && onOpenDetails(entry.details, entry.variant)}
            />
          ))
        )}
      </div>
    </div>
  );
});
